/* eslint-disable promise/always-return */
/* eslint-disable jsx-a11y/label-has-associated-control */

import classNames from 'classnames';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useAsync from 'react-use-async-hook';
import { Alert } from 'reactstrap';
import * as Yup from 'yup';
import * as authApi from '../../api/auth';
import logo from '../../assets/images/cl-logo.png';
import { useMsalDetails } from '../../authentication-context/msalContext';
import { Input } from '../../common/components';
import Loader from '../../common/components/loader/loader';
import { setServerPort, setServerUrl } from '../../common/hooks/server';
import { login as loginRoutine } from '../../common/routines/user-operations';
import { env, isAzureMfaEnabled } from '../../config/env';
import { emailRegex } from '../../constants/regex';
import validationMessages from '../../constants/validation-messages';

const Login = ({ isLoggedIn }) => {
  const { login: loginMessages } = validationMessages;
  const serverUrl = useSelector((state) => state.server.serverUrl);
  const serverPort = useSelector((state) => state.server.serverPort);
  const [azureServerUrl, setAzureServerUrl] = useState(serverUrl);
  const [azureServerPort, setAzureServerPort] = useState(serverPort || '');
  const [errorMessage, setErrorMessage] = useState();
  const {
    authError,
    authResult,
    clearAuthResult,
    isLoading: isMsalLoading,
    isReady,
    startLogin,
  } = useMsalDetails();

  const {
    data: response,
    loading,
    error,
    execute: doLogin,
  } = useAsync({
    autoExecute: false,
    task: authApi.login,
  });

  const handleSubmit = (values) => {
    setErrorMessage(undefined);
    doLogin({
      username: values.username,
      password: values.password,
    });
  };

  const handleAzureLogin = useCallback(async () => {
    setErrorMessage(undefined);
    setServerUrl(azureServerUrl);
    setServerPort(azureServerPort);
    await startLogin();
  }, [azureServerPort, azureServerUrl, startLogin]);

  useEffect(() => {
    if (authResult?.idToken) {
      doLogin({
        tokenId: authResult.idToken,
      });
      clearAuthResult();
    }
  }, [authResult, clearAuthResult, doLogin]);

  useEffect(() => {
    if (!error && response?.status === 200) {
      const { userDetails } = response.data;
      loginRoutine({
        userId: userDetails.ldapId,
        token: response.headers?.authtoken,
        email: userDetails.email,
        name: userDetails.name,
        location: userDetails.location,
        port: serverPort,
        clasArDashboard: userDetails?.clasArDashboard,
        userRole: userDetails?.userRoleId,
      });
    }

    if (error) {
      setErrorMessage(error?.response?.data?.message);
    }
  }, [error, response, serverPort]);

  useEffect(() => {
    if (authError) {
      setErrorMessage(authError.message || 'Microsoft sign-in failed');
    }
  }, [authError]);

  useEffect(() => {
    setAzureServerUrl(serverUrl);
    setAzureServerPort(serverPort || '');
  }, [serverPort, serverUrl]);

  // Auto-trigger Microsoft login redirect when MSAL is ready
  useEffect(() => {
    if (
      isAzureMfaEnabled?.toLowerCase() === 'true' &&
      isReady &&
      !isMsalLoading &&
      !isLoggedIn &&
      !loading &&
      !authResult &&
      !authError
    ) {
      handleAzureLogin();
    }
  }, [isReady, isMsalLoading, isLoggedIn, loading, authResult, authError]);

  return (
    <section className="login">
      {isAzureMfaEnabled?.toLowerCase() === 'true' ? (
        authError ? (
          <div className="content">
            <Alert color="danger" fade={false}>
              {errorMessage || 'Oops! Something Went Wrong'}
            </Alert>
            <div className="heading">
              <img src={logo} alt="hmis-app" className="cypresslawn-logo" />
              <p className="subject">Sign-in failed. Please try again.</p>
            </div>
            {env !== 'production' && env !== 'preprod' && (
              <>
                <div className={classNames('form-group', 'position-relative')}>
                  <label htmlFor="serverUrl">Server Endpoint</label>
                  <input
                    className={classNames('form-control')}
                    name="serverUrl"
                    required
                    value={azureServerUrl}
                    onChange={(event) => setAzureServerUrl(event.target.value)}
                    onBlur={(event) => setServerUrl(event.target.value)}
                  />
                </div>
                <div className={classNames('form-group', 'position-relative')}>
                  <label htmlFor="serverPort">Server Port</label>
                  <input
                    className={classNames('form-control')}
                    name="serverPort"
                    value={azureServerPort}
                    onChange={(event) => setAzureServerPort(event.target.value)}
                    onBlur={(event) => setServerPort(event.target.value)}
                  />
                </div>
              </>
            )}
            <button
              type="button"
              className={classNames('btn-theme', 'button')}
              disabled={loading || isMsalLoading || !isReady || isLoggedIn}
              onClick={handleAzureLogin}
            >
              {isMsalLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
            </button>
          </div>
        ) : (
          <Loader />
        )
      ) : (
        <div className="content">
          {error && (
            <Alert color="danger" fade={false}>
              {errorMessage || 'Oops! Something Went Wrong'}
            </Alert>
          )}
          <div className="heading">
            <img src={logo} alt="hmis-app" className="cypresslawn-logo" />
            <p className="subject">Please login with your email id to continue.</p>
          </div>
          <Formik
            initialValues={{
              username: '',
              password: '',
              serverUrl,
              serverPort,
            }}
            validationSchema={Yup.object({
              username: Yup.string()
                .required(loginMessages.email.REQUIRED)
                .matches(emailRegex, loginMessages.email.EMAIL_INVALID),
              password: Yup.string().required(loginMessages.password.REQUIRED),
            })}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleSubmit: onSubmit }) => {
              return (
                <Form onSubmit={onSubmit}>
                  <div className={classNames('form-group', 'position-relative')}>
                    <label htmlFor="username">Email ID</label>
                    <Field
                      name="username"
                      type="text"
                      className={classNames('form-control', {
                        'has-error': errors.username && touched.username,
                      })}
                      placeholder="john.dow@gmail.com"
                      disabled={loading}
                    />
                    <div className="error-msg">
                      <ErrorMessage name="username" />
                    </div>
                  </div>

                  <div className={classNames('form-group', 'position-relative')}>
                    <label htmlFor="password">Password</label>
                    <Field
                      name="password"
                      type="password"
                      className={classNames('form-control', {
                        'has-error': errors.password && touched.password,
                      })}
                      placeholder="************"
                      disabled={loading}
                    />
                    <div className="error-msg">
                      <ErrorMessage name="password" />
                    </div>
                  </div>

                  {env !== 'production' && env !== 'preprod' && (
                    <>
                      <div className={classNames('form-group', 'position-relative')}>
                        <label htmlFor="serverUrl">Server Endpoint</label>
                        <Input
                          formInput
                          name="serverUrl"
                          disabled={loading}
                          required
                          value={values.serverUrl}
                          onBlur={() => setServerUrl(values.serverUrl)}
                        />
                        <div className="error-msg">
                          <ErrorMessage name="serverUrl" />
                        </div>
                      </div>

                      <div className={classNames('form-group', 'position-relative')}>
                        <label htmlFor="serverPort">Server Port</label>
                        <Input
                          formInput
                          name="serverPort"
                          disabled={loading}
                          value={values.serverPort}
                          onBlur={() => setServerPort(values.serverPort)}
                        />
                        <div className="error-msg">
                          <ErrorMessage name="serverPort" />
                        </div>
                      </div>
                    </>
                  )}

                  <button
                    type="submit"
                    className={classNames('btn-theme', 'button')}
                    disabled={loading}
                  >
                    Login
                  </button>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
    </section>
  );
};

export default Login;
